import { Suspense, lazy, useEffect } from "react";
import ScrollToTop from "./helpers/scroll-top";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import WheelOfFortune from "./components/wheel-of-fortune/WheelOfFortune";
import cogoToast from "cogo-toast";
import { useDispatch } from "react-redux";
import { setPendingCoupon } from "./store/slices/cart-slice";

// custom hook to handle query params
const useQuery = () => {
  return new URLSearchParams(window.location.search);
}

// home pages
const HomeFashionFour = lazy(() => import("./pages/home/HomeFashionFour"));

// shop pages
const ShopGridStandard = lazy(() => import("./pages/shop/ShopGridStandard"));

// product pages
const ProductSticky = lazy(() => import("./pages/shop-product/ProductSticky"));

// other pages
const Contact = lazy(() => import("./pages/other/Contact"));
const TanitimKitapcigi = lazy(() => import("./pages/other/TanitimKitapcigi"));
const MyAccount = lazy(() => import("./pages/other/MyAccount"));

const Cart = lazy(() => import("./pages/other/Cart"));
const Wishlist = lazy(() => import("./pages/other/Wishlist"));
const Checkout = lazy(() => import("./pages/other/Checkout"));
const PaymentSuccess = lazy(() => import("./pages/other/PaymentSuccess"));
const PaymentFailure = lazy(() => import("./pages/other/PaymentFailure"));
const InformationDetail = lazy(() => import("./pages/other/InformationDetail"));
const NFCPhoneList = lazy(() => import("./pages/other/NFCPhoneList"));
const Events = lazy(() => import("./pages/other/Events"));
const GiveawayResults = lazy(() => import("./pages/other/GiveawayResults"));
const BirthChart = lazy(() => import("./pages/other/BirthChart"));
const HumanDesign = lazy(() => import("./pages/other/HumanDesign"));
const SkyEvents = lazy(() => import("./pages/other/SkyEvents"));
const PartnerLogin = lazy(() => import("./pages/other/PartnerLogin"));
const PartnerDashboard = lazy(() => import("./pages/other/PartnerDashboard"));
const WishTree = lazy(() => import("./pages/other/WishTree"));
const Astrologers = lazy(() => import("./pages/other/Astrologers"));
const AstrologerCollaboration = lazy(() => import("./pages/other/AstrologerCollaboration"));
const UserDashboard = lazy(() => import("./pages/other/UserDashboard"));
const AstrologerDashboard = lazy(() => import("./pages/other/AstrologerDashboard"));
const NotFound = lazy(() => import("./pages/other/NotFound"));

// Wrapper component to handle URL params inside Router context
const AppContent = () => {
  const dispatch = useDispatch();
  const query = useQuery();
  const refCode = query.get("ref");

  useEffect(() => {
    if (refCode) {
      dispatch(setPendingCoupon(refCode));
      cogoToast.success(`Partner kodu tanımlandı: ${refCode}`, { position: "bottom-right" });
    }
  }, [refCode, dispatch]);

  return (
    <ScrollToTop>
      <Suspense
        fallback={
          <div className="flone-preloader-wrapper">
            <div className="flone-preloader">
              <span></span>
              <span></span>
            </div>
          </div>
        }
      >
        {/* <WheelOfFortune /> */}
        <Routes>
          {/* Homepage */}
          <Route
            path={process.env.PUBLIC_URL + "/"}
            element={<HomeFashionFour />}
          />
          {/* Shop pages */}
          <Route
            path={process.env.PUBLIC_URL + "/collection"}
            element={<ShopGridStandard />}
          />

          {/* Shop product pages */}
          <Route
            path={process.env.PUBLIC_URL + "/collection/:slug"}
            element={<ProductSticky />}
          />

          {/* Other pages */}
          <Route
            path={process.env.PUBLIC_URL + "/contact"}
            element={<Contact />}
          />
          <Route
            path={process.env.PUBLIC_URL + "/nasil-kullanilir"}
            element={<TanitimKitapcigi />}
          />
          <Route
            path={process.env.PUBLIC_URL + "/my-account"}
            element={<MyAccount />}
          />
          <Route
            path={process.env.PUBLIC_URL + "/cart"}
            element={<Cart />}
          />
          <Route
            path={process.env.PUBLIC_URL + "/wishlist"}
            element={<Wishlist />}
          />
          <Route
            path={process.env.PUBLIC_URL + "/checkout"}
            element={<Checkout />}
          />

          {/* Payment result pages */}
          <Route
            path={process.env.PUBLIC_URL + "/payment/success"}
            element={<PaymentSuccess />}
          />
          <Route
            path={process.env.PUBLIC_URL + "/payment/failure"}
            element={<PaymentFailure />}
          />

          {/* Information pages */}
          <Route
            path={process.env.PUBLIC_URL + "/p/:slug"}
            element={<InformationDetail />}
          />

          {/* NFC Phone List */}
          <Route
            path={process.env.PUBLIC_URL + "/nfc-destekleyen-telefonlar"}
            element={<NFCPhoneList />}
          />

          {/* Events */}
          <Route
            path={process.env.PUBLIC_URL + "/events"}
            element={<Events />}
          />

          {/* Giveaway Results */}
          <Route
            path={process.env.PUBLIC_URL + "/results/2025"}
            element={<GiveawayResults />}
          />

          {/* Birth Chart */}
          <Route
            path={process.env.PUBLIC_URL + "/birth-chart"}
            element={<BirthChart />}
          />

          {/* Human Design */}
          <Route
            path={process.env.PUBLIC_URL + "/human-design"}
            element={<HumanDesign />}
          />

          {/* Sky Events */}
          <Route
            path={process.env.PUBLIC_URL + "/gokyuzu-seyri"}
            element={<SkyEvents />}
          />

          {/* Wish Tree */}
          <Route
            path={process.env.PUBLIC_URL + "/wish-tree"}
            element={<WishTree />}
          />

          {/* Astrologers (Geçici olarak gizlendi) */}
          {/* <Route
            path={process.env.PUBLIC_URL + "/astrologlar"}
            element={<Astrologers />}
          />
          <Route
            path={process.env.PUBLIC_URL + "/astrologlar/is-birligi"}
            element={<AstrologerCollaboration />}
          /> */}

          {/* Partner Login */}
          <Route
            path={process.env.PUBLIC_URL + "/login"}
            element={<PartnerLogin />}
          />
          {/* Partner Dashboard */}
          <Route
            path={process.env.PUBLIC_URL + "/partner/dashboard"}
            element={<PartnerDashboard />}
          />
          <Route
            path={process.env.PUBLIC_URL + "/astrolog/dashboard"}
            element={<AstrologerDashboard />}
          />
          <Route
            path={process.env.PUBLIC_URL + "/user/dashboard"}
            element={<UserDashboard />}
          />

          {/* 404 page */}

          <Route
            path={process.env.PUBLIC_URL + "/404"}
            element={<NotFound />}
          />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </ScrollToTop>
  );
}

const App = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default App;